import { JWTPayload, decodeJwt } from "jose";
import { GailProductRoles, UserRoles } from "@/enums/user";
import { TOKEN } from "@/types/common/ids";
import { CustomRole } from "@/types/user";
import { IsSuperAdmin } from "./admin";
import { BASE_GAIL } from "./baseUrls";

const TOKEN_HOST = process.env.NEXT_PUBLIC_AUTH_TOKEN_HOST!;

export type ProductRolesState = {
  roles: GailProductRoles[] | undefined;
  loaded: boolean;
};

const decodeToken = (accessToken: TOKEN): JWTPayload => {
  if (!accessToken) {
    return {};
  }

  return decodeJwt(accessToken);
};

export const IsPrivileged = (accessToken: TOKEN): boolean => {
  const tokenPayload = decodeToken(accessToken);

  if (
    tokenPayload &&
    tokenPayload.email &&
    typeof tokenPayload.email === "string" &&
    IsSuperAdmin({ email: tokenPayload.email as string })
  ) {
    return true;
  }

  return false;
};

/**
 * Gets the internal organization/agency ID from the access token.
 * This reads from the namespaced ${TOKEN_HOST}/org_id claim.
 *
 * Note: This is the internal org/agency ID, not the Auth0 Organization ID.
 * For the Auth0 Organization ID, use GetOrgId().
 */
export const GetAgencyId = (accessToken: TOKEN): string | null => {
  const tokenPayload = decodeToken(accessToken);

  // Read from namespaced org_id claim (internal org/agency ID)
  const orgId = tokenPayload[`${TOKEN_HOST}/org_id`];
  if (orgId && typeof orgId === "string") {
    return orgId;
  }

  return null;
};

export const GetOrgId = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (tokenPayload?.org_id && typeof tokenPayload.org_id === "string") {
    return tokenPayload.org_id;
  }

  return "";
};

/**
 * @deprecated This function previously looked up org name from user_orgs in the token.
 * Since user_orgs is no longer stored in tokens, this now returns an empty string.
 * Use the org context or API to get organization display names.
 */
export const GetOrgName = (accessToken: TOKEN): string => {
  // user_orgs is no longer available in the token
  // Return empty string - callers should use org context or API for display names
  return "";
};

export const GetRole = (accessToken: TOKEN): UserRoles => {
  const tokenPayload = decodeToken(accessToken);

  if (
    tokenPayload &&
    tokenPayload[`${TOKEN_HOST}/roles`] &&
    (tokenPayload[`${TOKEN_HOST}/roles`] as any[]).length > 0
  ) {
    const allRoles = tokenPayload[`${TOKEN_HOST}/roles`] as string[];
    const userRoles = allRoles
      .map((role) => role.replaceAll(" ", ""))
      .filter((role) => Object.values(UserRoles).includes(role as UserRoles));

    if (userRoles.length > 0) {
      return userRoles[0] as UserRoles;
    }
  }

  return UserRoles.Admin;
};

/**
 * Extracts a standard role from the token if one exists.
 * Unlike GetRole, this returns null instead of defaulting to Admin when no standard role is found.
 * This is useful for determining if an API call is needed to fetch custom roles.
 *
 * @param accessToken The access token
 * @returns The UserRoles value if a standard role is found, or null if no standard role exists (indicating a custom role)
 */
export const GetStandardRoleFromToken = (accessToken: TOKEN): UserRoles | null => {
  const tokenPayload = decodeToken(accessToken);

  if (
    tokenPayload &&
    tokenPayload[`${TOKEN_HOST}/roles`] &&
    (tokenPayload[`${TOKEN_HOST}/roles`] as any[]).length > 0
  ) {
    const allRoles = tokenPayload[`${TOKEN_HOST}/roles`] as string[];
    const userRoles = allRoles
      .map((role) => role.replaceAll(" ", ""))
      .filter((role) => Object.values(UserRoles).includes(role as UserRoles));

    if (userRoles.length > 0) {
      return userRoles[0] as UserRoles;
    }
  }

  return null;
};

/**
 * Parses product-seat names from the JWT `roles` claim. Authorization must not
 * use this — seats come from `getProductRoles` in `@/utils/authz/fetchMyScopes`.
 */
export const GetProductRoles = (accessToken: TOKEN): GailProductRoles[] => {
  const tokenPayload = decodeToken(accessToken);
  const roles = tokenPayload[`${TOKEN_HOST}/roles`] as string[] | undefined;

  // Handle cases where roles don't exist in older tokens
  if (!roles || !Array.isArray(roles)) {
    return [];
  }

  return roles.filter((role) =>
    Object.values(GailProductRoles).includes(role as GailProductRoles)
  ) as GailProductRoles[];
};

/**
 * Extracts the role ID from the token.
 * Filters out product roles and UserRoles enum values.
 * If a custom role is found (by matching internalName), returns its ID.
 * Otherwise returns null.
 *
 * @param accessToken The access token
 * @param customRoles Optional list of custom roles to match against
 * @returns The role ID (UUID for custom roles) or null
 */
export const GetRoleId = (accessToken: TOKEN, customRoles?: CustomRole[]): string | null => {
  const tokenPayload = decodeToken(accessToken);
  const roles = tokenPayload[`${TOKEN_HOST}/roles`] as string[] | undefined;

  // Handle cases where roles don't exist in older tokens
  if (!roles || !Array.isArray(roles) || roles.length === 0) {
    return null;
  }

  // Filter out product roles and UserRoles enum values
  const remainingRoles = roles.filter(
    (role) =>
      !Object.values(GailProductRoles).includes(role as GailProductRoles) &&
      !Object.values(UserRoles).includes(role as UserRoles)
  );

  // If no remaining roles, return null
  if (remainingRoles.length === 0) {
    return null;
  }

  // If we have custom roles to match against, try to find a match by authRoleName first
  // (authRoleName is the field that matches what's in the JWT)
  if (customRoles && customRoles.length > 0) {
    // Try matching by authRoleName first
    let matchedRole = customRoles.find(
      (customRole) => customRole.authRoleName === remainingRoles[0]
    );

    if (matchedRole && matchedRole.id) {
      return matchedRole.id as string;
    }
  }

  // If no custom role match found, return null
  // (The remaining role is likely an internalName but we don't have the custom roles list)
  return null;
};

export const GetEmail = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (tokenPayload && tokenPayload.email && typeof tokenPayload.email === "string") {
    return tokenPayload.email;
  }

  return "";
};

export const GetFirstName = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (tokenPayload && tokenPayload.given_name && typeof tokenPayload.given_name === "string") {
    return tokenPayload.given_name;
  }

  return "";
};

export const GetLastName = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (tokenPayload && tokenPayload.family_name && typeof tokenPayload.family_name === "string") {
    return tokenPayload.family_name;
  }

  return "";
};

export const GetUserId = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (tokenPayload && tokenPayload.sub && typeof tokenPayload.sub === "string") {
    return tokenPayload.sub;
  }

  return "";
};

export const GetLulaUserId = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (
    tokenPayload &&
    tokenPayload[`${TOKEN_HOST}/user_id`] &&
    typeof tokenPayload[`${TOKEN_HOST}/user_id`] === "string"
  ) {
    return tokenPayload[`${TOKEN_HOST}/user_id`] as string;
  }

  return "";
};

export const CalUserRefreshEndpoint = (accessToken: TOKEN) => {
  const orgId = GetAgencyId(accessToken);
  const auth0UserId = GetUserId(accessToken);

  return `${BASE_GAIL}/api/calendar/refresh/${orgId}/${auth0UserId}`;
};

/**
 * Compares two arrays of roles for equality.
 * Order doesn't matter - arrays are sorted before comparison.
 *
 * @param roles1 First array of roles to compare
 * @param roles2 Second array of roles to compare
 * @returns true if the arrays contain the same roles, false otherwise
 */
export const areRolesEqual = (
  roles1: GailProductRoles[] | undefined,
  roles2: GailProductRoles[] | undefined
): boolean => {
  if (roles1 === roles2) return true;
  if (!roles1 || !roles2) return false;
  if (roles1.length !== roles2.length) return false;

  // Sort both arrays for comparison (order shouldn't matter for roles)
  const sorted1 = [...roles1].sort();
  const sorted2 = [...roles2].sort();

  return sorted1.every((role, index) => role === sorted2[index]);
};

/**
 * Compares two ProductRolesState objects for equality.
 *
 * @param state1 First ProductRolesState to compare
 * @param state2 Second ProductRolesState to compare
 * @returns true if both states have the same loaded status and roles, false otherwise
 */
export const areProductRolesStatesEqual = (
  state1: ProductRolesState,
  state2: ProductRolesState
): boolean => {
  return state1.loaded === state2.loaded && areRolesEqual(state1.roles, state2.roles);
};
